/* Detail Theory - dilution and PIR calculator.
 *
 * RATIO CONVENTION. Ratios read X:1, X parts water to 1 part product, the way
 * Detail Theory's own product descriptions do (P&S Absolute states 256:1 as
 * "30ml in 7.5L of water"). The mix is X+1 parts total:
 *
 *   product = total / (ratio + 1)
 *   water   = total - product
 *
 * One catalogue product writes it backwards (Yum Dress, "dilute approximately
 * 1:5") - a copy problem, not a maths one, and the note under the calculator
 * warns about it.
 *
 * PIR. Panel Impact Ratio is the concentrate as a percentage of everything
 * that leaves a foam cannon: bottle water plus the pressure washer's water
 * together. A cannon siphons from its bottle into the washer's flow, so a 1L
 * bottle can put 11L on the car, and a ratio mixed in the bottle says nothing
 * about what reaches the paint. Agitator, plunger and orifice all move the
 * number, so each setup gets measured once:
 *
 *   concentrate = measured output * (pir / 100)
 *   water       = bottle capacity - concentrate
 *
 * Concentrate can exceed the bottle at a high PIR on a thirsty setup. That's
 * a real answer, not an error, so the panel says the setup cannot reach that
 * PIR instead of printing a negative water figure.
 *
 * PRESETS WRITE INTO THE INPUTS. A chip is a shortcut, not a second source of
 * truth: clicking one fills the number field, and it shows pressed only while
 * the field still holds its value, so every preset stays editable by hand.
 *
 * Everything recalculates on input - no submit button to miss.
 */
(function () {
  'use strict';

  function round(value, places) {
    var factor = Math.pow(10, places);
    return Math.round(value * factor) / factor;
  }

  /* Fields are type="text" so a comma reaches us: on type="number" Chrome
     drops the comma keypress and "1,5" arrives as 15, a tenfold overdose
     printed with no error. Number rather than parseFloat, because parseFloat
     reads "10abc" as 10 and stops, sailing a bad value through as real. */
  /* Trailing dot allowed: "1." is "1.5" half typed, and an error that flashes
     for one keystroke reads as the calculator arguing with you. */
  var DECIMAL = /^(\d+\.?\d*|\.\d+)$/;

  function toNumber(raw) {
    var text = String(raw).trim().replace(',', '.');
    /* Plain decimals only: Number() reads "0x10" as 16 and "1e5" as 100000. */
    return DECIMAL.test(text) ? Number(text) : NaN;
  }

  /* US fluid ounce and US gallon; the imperial gallon is 20% larger, which is
     the difference between a mix that works and one that does not. */
  var ML_PER_FLOZ = 29.5735;
  var FLOZ_PER_GAL = 128;

  /* Chip values come from staff-editable metafields; same guard Liquid
     applies to its own copy of this list. */
  function escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, function (character) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character];
    });
  }

  function isImperial(unit) {
    return !!unit && (unit.value === 'floz' || unit.value === 'gal');
  }

  /* The big number is whole millilitres, litres past a thousand. In fluid
     ounces a tenth is worth keeping, because a whole ounce is 30 ml. */
  function formatVolume(ml, imperial) {
    if (imperial) {
      var floz = ml / ML_PER_FLOZ;
      if (floz >= FLOZ_PER_GAL) return round(floz / FLOZ_PER_GAL, 2) + ' gal';
      /* Same never-print-0 rule as millilitres below. */
      var tenth = round(floz, 1);
      if (tenth === 0 && floz > 0) return round(floz, 3) + ' fl oz';
      return tenth + ' fl oz';
    }
    var whole = Math.round(ml);
    if (whole >= 1000) return round(whole / 1000, 2) + ' L';

    /* Never print 0 for something a person still has to pour. Bilt Hamber
       Auto-Wash is 2000:1, so a 1 L bottle takes half a millilitre, and
       rounding to the nearest millilitre would read "Pour 0 ml". Below a
       millilitre the decimal IS the answer, so rounding stops there. */
    if (whole === 0 && ml > 0) return round(ml, 2) + ' ml';

    return whole + ' ml';
  }

  /* Extra precision for anyone measuring into a syringe; empty when it would
     only repeat the big number. */
  function formatAlt(ml, imperial) {
    if (imperial) {
      var floz = ml / ML_PER_FLOZ;
      if (floz >= FLOZ_PER_GAL) return '(' + round(floz, 1) + ' fl oz)';
      var fine = round(floz, 2);
      return fine === round(floz, 1) ? '' : '(' + fine + ' fl oz)';
    }
    var whole = Math.round(ml);
    if (whole >= 1000) return '(' + whole + ' ml)';
    var precise = round(ml, 1);
    return precise === whole ? '' : '(' + precise + ' ml)';
  }

  function positive(input) {
    var value = input ? toNumber(input.value) : NaN;
    return isFinite(value) && value > 0 ? value : 0;
  }

  /* Ratio 0 is Neat, a real answer, not "nothing typed" - positive() cannot
     tell those apart, so this returns null for unusable instead. */
  function ratioOf(input) {
    var raw = input ? String(input.value).trim() : '';
    if (raw === '') return null;
    var value = toNumber(raw);
    return isFinite(value) && value >= 0 ? value : null;
  }

  /* Everything is held in millilitres internally. */
  function millilitres(input, unit) {
    var value = positive(input);
    if (!value) return 0;
    if (!unit) return value;
    if (unit.value === 'l') return value * 1000;
    if (unit.value === 'floz') return value * ML_PER_FLOZ;
    if (unit.value === 'gal') return value * ML_PER_FLOZ * FLOZ_PER_GAL;
    return value;
  }

  if (customElements.get('dilution-calculator')) return;

  customElements.define(
    'dilution-calculator',
    class extends HTMLElement {
      connectedCallback() {
        this.update = this.update.bind(this);

        this.tabs = Array.prototype.slice.call(this.querySelectorAll('[data-tab]'));
        this.panels = Array.prototype.slice.call(this.querySelectorAll('[data-panel]'));
        this.tabs.forEach(
          function (tab, index) {
            tab.addEventListener('click', this.selectTab.bind(this, index));
            tab.addEventListener('keydown', this.onTabKey.bind(this, index));
          }.bind(this)
        );

        this.fields = {};
        this.units = {};
        ['size', 'ratio', 'output', 'capacity', 'pir'].forEach(
          function (name) {
            var field = this.querySelector('[data-field="' + name + '"]');
            var unit = this.querySelector('[data-unit-for="' + name + '"]');
            if (field) {
              this.fields[name] = field;
              field.addEventListener('input', this.update);
            }
            if (unit) {
              this.units[name] = unit;
              unit.addEventListener('change', this.update);
            }
          }.bind(this)
        );

        this.collectChips();

        Array.prototype.slice.call(this.querySelectorAll('[data-tip]')).forEach(function (tip) {
          tip.addEventListener('click', function () {
            var open = tip.getAttribute('aria-expanded') === 'true';
            var hint = document.getElementById(tip.getAttribute('aria-controls'));
            tip.setAttribute('aria-expanded', open ? 'false' : 'true');
            if (hint) hint.hidden = open;
          });
        });

        /* Slider and field are two handles on one value. The slider's range
           is deliberately narrower than the field's, so out-of-range values
           disable it rather than pinning silently to its own max (two
           answers on screen, the next drag destroying the typed one). */
        this.sliderSyncs = [];
        Array.prototype.slice.call(this.querySelectorAll('[data-slider-for]')).forEach(
          function (slider) {
            var field = this.fields[slider.getAttribute('data-slider-for')];
            if (!field) return;

            var sync = function () {
              var value = toNumber(field.value);
              /* An unreadable bound is no bound. Comparing against NaN is
                 false either way, so a blank min would read every value as
                 out of range and disable the slider for good. */
              var low = toNumber(slider.min);
              var high = toNumber(slider.max);
              var reachable =
                isFinite(value) &&
                (!isFinite(low) || value >= low) &&
                (!isFinite(high) || value <= high);
              slider.disabled = !reachable;
              /* The normalised number, not the raw field text: a range input
                 cannot read "1,5" and snaps to its midpoint rather than
                 refusing, which moves the thumb to a value nobody chose. */
              if (reachable) slider.value = value;
            };

            slider.addEventListener('input', function () {
              field.value = slider.value;
              this.update();
            }.bind(this));
            field.addEventListener('input', sync);
            this.sliderSyncs.push(sync);
          }.bind(this)
        );

        /* Clamp is a class, not DOM removal, so find-in-page still works; the
           two labels ride on the button as data attributes since the client owns the words. */
        Array.prototype.slice.call(this.querySelectorAll('[data-more-toggle]')).forEach(function (button) {
          button.addEventListener('click', function () {
            var wrapper = button.closest('[data-more]');
            if (!wrapper) return;
            var open = wrapper.classList.toggle('is-open');
            button.setAttribute('aria-expanded', open ? 'true' : 'false');
            button.textContent = open ? button.getAttribute('data-label-less') : button.getAttribute('data-label-more');
          });
        });

        this.showChosen(null);
        this.bindLookup();
        this.bindReset();

        this.syncMoreToggles();
        this.update();

        /* Bottle-figure visibility is a layout measurement, and a webfont
           swap, a panel unhiding, or a resize can all move that layout after
           the first pass. So it reruns on the next frame and on real bottle
           resizes; cheap, since update() just writes the same values back. */
        requestAnimationFrame(this.update);
        this.watchVessels();
      }

      /* Client-side filtering over the theme-rendered list, not a request per
         keystroke. Plain substring match - no fuzzy matching, since a near
         miss returning the wrong ratio is worse than no match. */
      bindLookup() {
        var search = this.querySelector('[data-lookup-search]');
        var items = Array.prototype.slice.call(this.querySelectorAll('[data-lookup-item]'));
        var empty = this.querySelector('[data-lookup-empty]');
        if (!items.length) return;

        if (search) {
          search.addEventListener('input', function () {
            var term = search.value.trim().toLowerCase();
            var shown = 0;
            items.forEach(function (item) {
              var match = !term || item.getAttribute('data-search').indexOf(term) !== -1;
              item.hidden = !match;
              if (match) shown++;
            });
            if (empty) empty.hidden = shown > 0;
          });
        }

        items.forEach(
          function (item) {
            var button = item.querySelector('[data-lookup-pick]');
            if (!button) return;
            button.addEventListener(
              'click',
              function () {
                if (this.fields.ratio) this.fields.ratio.value = button.getAttribute('data-ratio');
                this.setRatioPresets(button.getAttribute('data-ratios'));
                this.showChosen(button);
                this.update();
                if (this.tabs.length) this.selectTab(0);
              }.bind(this)
            );
          }.bind(this)
        );

        /* Change restores the ratio too - the label back with the borrowed
           number left in place would read as this product at someone else's
           strength. */
        var clear = this.querySelector('[data-chosen-clear]');
        if (clear) {
          var startingRatio = this.fields.ratio ? this.fields.ratio.value : null;
          clear.addEventListener(
            'click',
            function () {
              if (this.fields.ratio && startingRatio !== null) this.fields.ratio.value = startingRatio;
              this.setRatioPresets(null);
              this.showChosen(null);
              this.update();
              if (this.tabs.length > 2) this.selectTab(this.tabs.length - 1);
            }.bind(this)
          );
        }
      }

      /* The How strong chips become the picked product's own ratios. Pass
         null (Change, Reset) to put the page's original markup back rather
         than rebuild it. */
      setRatioPresets(json) {
        var host = this.querySelector('[data-ratio-presets]');
        if (!host) return;

        if (this.presetsInitial === undefined) this.presetsInitial = host.innerHTML;

        if (!json) {
          host.innerHTML = this.presetsInitial;
          this.collectChips();
          return;
        }

        var rows;
        try {
          rows = JSON.parse(json);
        } catch (error) {
          return;
        }

        host.innerHTML = rows
          .map(
            function (row) {
              var caption = row[1]
                ? '<span class="dilution-calc__chip-label">' + escapeHtml(row[1]) + '</span>'
                : '';
              return (
                '<button type="button" class="dilution-calc__chip" aria-pressed="false" data-preset="ratio" data-value="' +
                escapeHtml(row[0]) +
                '"><span class="dilution-calc__chip-value">' +
                escapeHtml(this.ratioLabel(row[0])) +
                '</span>' +
                caption +
                '</button>'
              );
            }.bind(this)
          )
          .join('');

        this.collectChips();
      }

      /* See the ratio convention note in the header - JS copy of the rule
         Liquid applies to the chips it renders itself. */
      ratioLabel(value) {
        if (toNumber(value) === 0) return this.getAttribute('data-neat-label') || 'Neat';
        return value + ':1';
      }

      /* Chips are re-read rather than held from connect, because the ratio
         set is replaced whenever a product is picked. */
      collectChips() {
        this.chips = Array.prototype.slice.call(this.querySelectorAll('[data-preset]'));
        this.chips.forEach(
          function (chip) {
            if (chip.dataset.bound) return;
            chip.dataset.bound = '1';
            chip.addEventListener(
              'click',
              function () {
                var name = chip.getAttribute('data-preset');
                if (!this.fields[name]) return;
                this.fields[name].value = chip.getAttribute('data-value');
                var unit = this.units[name];
                if (unit && chip.getAttribute('data-unit')) unit.value = chip.getAttribute('data-unit');
                this.update();
              }.bind(this)
            );
          }.bind(this)
        );
      }

      /* Pass null to restore what the page rendered with (a product page's
         own product), not an empty panel. */
      showChosen(button) {
        var panel = this.querySelector('[data-chosen]');
        if (!panel) return;

        var image = panel.querySelector('[data-chosen-image]');
        var name = panel.querySelector('[data-chosen-name]');
        var use = panel.querySelector('[data-chosen-use]');

        if (!this.chosenInitial) {
          this.chosenInitial = {
            hidden: panel.hidden,
            src: image ? image.getAttribute('src') : '',
            alt: image ? image.getAttribute('alt') : '',
            imageHidden: image ? image.hidden : true,
            name: name ? name.textContent : '',
            href: name ? name.getAttribute('href') : '#',
            use: use ? use.textContent : '',
          };
        }

        if (!button) {
          var start = this.chosenInitial;
          if (image) {
            image.src = start.src;
            image.alt = start.alt;
            image.hidden = start.imageHidden;
          }
          if (name) {
            name.textContent = start.name;
            name.href = start.href;
          }
          if (use) use.textContent = start.use;
          panel.hidden = start.hidden;
          return;
        }

        var title = button.getAttribute('data-title') || '';
        var source = button.getAttribute('data-image');

        if (image) {
          /* No image is a real state: a product can have none. Hiding the
             element beats a broken-image icon or a grey box. */
          image.hidden = !source;
          if (source) {
            image.src = source;
            image.alt = title;
          }
        }
        if (name) {
          name.textContent = title;
          name.href = button.getAttribute('data-url') || '#';
        }
        if (use) {
          var caption = button.getAttribute('data-use');
          var ratio = this.ratioLabel(button.getAttribute('data-ratio'));
          use.textContent = caption ? ratio + ', ' + caption : ratio;
        }

        panel.hidden = false;
      }

      /* Reset restores the page's load state rather than emptying the
         fields - emptying would throw away a product page's own ratio. */
      bindReset() {
        var button = this.querySelector('[data-reset]');
        if (!button) return;

        var initial = [];
        Object.keys(this.fields).forEach(
          function (name) {
            initial.push([this.fields[name], this.fields[name].value]);
          }.bind(this)
        );
        Object.keys(this.units).forEach(
          function (name) {
            initial.push([this.units[name], this.units[name].value]);
          }.bind(this)
        );

        button.addEventListener(
          'click',
          function () {
            initial.forEach(function (pair) {
              pair[0].value = pair[1];
            });

            var search = this.querySelector('[data-lookup-search]');
            if (search) {
              search.value = '';
              search.dispatchEvent(new Event('input'));
            }

            this.setRatioPresets(null);
            this.showChosen(null);
            this.update();
          }.bind(this)
        );
      }

      /* The guard is what stops the observer feeding itself: update() sets
         flex-grow, which resizes the bands, which fires the observer. */
      watchVessels() {
        if (typeof ResizeObserver === 'undefined') return;

        var busy = false;
        var observer = new ResizeObserver(
          function () {
            if (busy) return;
            busy = true;
            requestAnimationFrame(
              function () {
                this.update();
                busy = false;
              }.bind(this)
            );
          }.bind(this)
        );

        Array.prototype.slice.call(this.querySelectorAll('[data-vessel]')).forEach(function (vessel) {
          if (vessel.hasAttribute('data-vessel-out')) return;
          observer.observe(vessel);
        });
      }

      /* The clamp is CSS, so measuring is the only way to know if it clipped.
         Can't run once at startup: the PIR panel stays hidden until its tab
         is chosen, and a hidden element measures zero, hiding every toggle.
         So this reruns on each tab change. */
      syncMoreToggles() {
        Array.prototype.slice.call(this.querySelectorAll('[data-more-toggle]')).forEach(function (button) {
          var wrapper = button.closest('[data-more]');
          var content = wrapper ? wrapper.querySelector('[data-more-content]') : null;
          if (!content) return;
          if (wrapper.classList.contains('is-open') || !content.offsetParent) return;
          button.hidden = content.scrollHeight <= content.clientHeight + 1;
        });
      }

      selectTab(index) {
        this.tabs.forEach(function (tab, i) {
          tab.setAttribute('aria-selected', i === index ? 'true' : 'false');
          tab.tabIndex = i === index ? 0 : -1;
        });
        this.panels.forEach(function (panel, i) {
          panel.hidden = i !== index;
        });
        this.syncMoreToggles();

        /* The panel just made visible measured zero while hidden, so recompute
           now that it has a real height. */
        this.update();
        this.tabs[index].focus();
      }

      onTabKey(index, event) {
        var step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
        if (!step) return;
        event.preventDefault();
        this.selectTab((index + step + this.tabs.length) % this.tabs.length);
      }

      /* The bar is always drawn to scale, no floor under the concentrate
         band, no caption. At 2000:1 the band is a fraction of a pixel and
         vanishes - the honest picture, since the readouts beside the bar
         still carry both figures. */
      drawVessel(scope, product, water, imperial) {
        var vessel = scope.querySelector('[data-vessel]');
        if (!vessel) return;

        var parts = {
          product: vessel.querySelector('[data-vessel="product"]'),
          water: vessel.querySelector('[data-vessel="water"]'),
        };
        var total = product === null ? 0 : product + water;

        /* Empty, not full. With no numbers to draw, a bottle filled to the
           neck with water is a reading in itself, and the wrong one. */
        if (!total) {
          parts.product.style.flexGrow = '0';
          parts.water.style.flexGrow = '0';
          Object.keys(parts).forEach(function (key) {
            parts[key].querySelector('[data-vessel-out="' + key + '"]').textContent = '';
          });
          return;
        }

        var share = product / total;

        parts.product.style.flexGrow = round(share, 5);
        parts.water.style.flexGrow = round(1 - share, 5);

        var figures = { product: product, water: water };
        Object.keys(parts).forEach(function (key) {
          var band = parts[key];
          band.querySelector('[data-vessel-out="' + key + '"]').textContent = formatVolume(figures[key], imperial);
          /* 24px is one line of the figure's own type plus its padding.
             Under that the digits would be clipped rather than shrunk, and
             the readout beside the bar still carries the number. */
          if (band.getBoundingClientRect().height < 24) {
            band.setAttribute('data-too-small', '');
          } else {
            band.removeAttribute('data-too-small');
          }
        });
      }

      write(mode, product, water, summary, imperial) {
        var scope = this.querySelector('[data-results="' + mode + '"]');
        if (!scope) return;
        var blank = product === null;
        var values = {
          product: blank ? '--' : formatVolume(product, imperial),
          'product-alt': blank ? '' : formatAlt(product, imperial),
          water: blank ? '--' : formatVolume(water, imperial),
          'water-alt': blank ? '' : formatAlt(water, imperial),
          summary: summary,
        };
        Object.keys(values).forEach(function (key) {
          var el = scope.querySelector('[data-out="' + key + '"]');
          if (el) el.textContent = values[key];
        });

        this.drawVessel(scope, product, water, imperial);
      }

      markChips() {
        this.chips.forEach(
          function (chip) {
            var name = chip.getAttribute('data-preset');
            var field = this.fields[name];
            if (!field) return; /* reading .value off nothing would take the panel down */
            var unit = this.units[name];
            var match = toNumber(chip.getAttribute('data-value')) === toNumber(field.value);
            if (match && unit && chip.getAttribute('data-unit')) {
              match = chip.getAttribute('data-unit') === unit.value;
            }
            chip.setAttribute('aria-pressed', match ? 'true' : 'false');
          }.bind(this)
        );
      }

      /* A client-editable ceiling, falling back to the code's own number. */
      limit(attribute, fallback) {
        var value = toNumber(this.getAttribute(attribute));
        return isFinite(value) && value > 0 ? value : fallback;
      }

      fill(attribute, values) {
        return Object.keys(values).reduce(function (text, key) {
          return text.replace('[' + key + ']', values[key]);
        }, this.getAttribute(attribute) || '');
      }

      /* Why a field refuses to compute has to be said out loud, rather than
         the same silent dashes for empty, zero, negative and overflow alike.
         An empty field is not an error though - it's the state every field
         starts in, and shouting for not having typed yet is worse than
         saying nothing. */
      checkField(name) {
        var field = this.fields[name];
        if (!field) return '';

        var raw = String(field.value).trim();
        if (raw === '') return '';

        var value = toNumber(raw);
        /* Only a ratio may be 0 (Neat); elsewhere 0 is still filled in wrongly. */
        var zeroAllowed = name === 'ratio';
        if (!isFinite(value) || value < 0 || (value === 0 && !zeroAllowed)) {
          return this.getAttribute('data-error-positive') || '';
        }

        /* Ceilings are settings, not numbers, so the copy that explains one
           can't drift from what the code enforces. */
        if (name === 'pir') {
          if (value > this.limit('data-max-pir', 100)) return this.getAttribute('data-error-pir') || '';
          return '';
        }

        if (name === 'ratio') {
          if (value > this.limit('data-max-ratio', 100000)) {
            return this.getAttribute('data-error-ratio-large') || '';
          }
          return '';
        }

        /* Past this the customer has picked the wrong unit rather than found
           a bigger bucket. Stated in litres, held in millilitres. */
        if (millilitres(field, this.units[name]) > this.limit('data-max-litres', 1000) * 1000) {
          return this.getAttribute('data-error-large') || '';
        }

        return '';
      }

      validate(names) {
        return names.reduce(
          function (ok, name) {
            var message = this.checkField(name);
            var slot = this.querySelector('[data-error-for="' + name + '"]');
            var field = this.fields[name];

            if (slot) {
              slot.textContent = message;
              slot.hidden = !message;
            }
            if (field) {
              if (message) {
                field.setAttribute('aria-invalid', 'true');
              } else {
                field.removeAttribute('aria-invalid');
              }
            }
            return ok && !message;
          }.bind(this),
          true
        );
      }

      update() {
        /* Every path that changes a field lands here, so sliders sync from this one place. */
        if (this.sliderSyncs) this.sliderSyncs.forEach(function (sync) { sync(); });
        this.markChips();
        this.updateDilution();
        this.updatePir();
      }

      updateDilution() {
        if (!this.fields.size) return;

        /* The answer is read back in whatever the customer measured the
           container in. Someone who typed gallons does not want millilitres. */
        var imperial = isImperial(this.units.size);
        var valid = this.validate(['size', 'ratio']);
        var total = millilitres(this.fields.size, this.units.size);
        var ratio = ratioOf(this.fields.ratio);

        if (!valid || !total || ratio === null) return this.write('dilution', null, null, '', imperial);

        var product = total / (ratio + 1);
        return this.write(
          'dilution',
          product,
          total - product,
          this.fill(ratio === 0 ? 'data-summary-neat' : 'data-summary-dilution', {
            product: formatVolume(product, imperial),
            total: formatVolume(total, imperial),
            ratio: round(ratio, 2),
          }),
          imperial
        );
      }

      updatePir() {
        if (!this.fields.pir) return;

        /* The cannon bottle is what gets filled, so its unit is the one the
           answer is read back in, not the measured output's. */
        var imperial = isImperial(this.units.capacity);
        var valid = this.validate(['output', 'capacity', 'pir']);
        var output = millilitres(this.fields.output, this.units.output);
        var capacity = millilitres(this.fields.capacity, this.units.capacity);
        var pir = positive(this.fields.pir);

        if (!valid || !output || !capacity || !pir) return this.write('pir', null, null, '', imperial);

        var concentrate = output * (pir / 100);

        if (concentrate > capacity) {
          return this.write(
            'pir',
            null,
            null,
            this.fill('data-summary-over', {
              pir: round(pir, 2),
              capacity: formatVolume(capacity, imperial),
              product: formatVolume(concentrate, imperial),
            }),
            imperial
          );
        }

        return this.write(
          'pir',
          concentrate,
          capacity - concentrate,
          this.fill('data-summary-pir', {
            product: formatVolume(concentrate, imperial),
            capacity: formatVolume(capacity, imperial),
            output: formatVolume(output, imperial),
            pir: round(pir, 2),
          }),
          imperial
        );
      }
    }
  );
})();
